<template>
  <div class="no-tabs notifications-settings">
    <DxDataGrid
      ref="notificationsSettingsGrid"
      :data-source="customDataSource"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :column-hiding-enabled="true"
      :hover-state-enabled="true"
      :remote-operations="{ paging: true, sorting: true, filtering: true }"
      :show-borders="true"
      :show-column-lines="true"
      :scrolling="scrollingConfig"
      @selection-changed="onSelectionChanged"
      class="notifications-settings-grid"
    >
      <DxSelection
        v-if="adminOrBoss"
        :show-check-boxes-mode="showCheckBoxesMode"
        mode="multiple"
      />
      <DxHeaderFilter :visible="true" />
      <DxPaging :page-size="10" />
      <DxPager
        :allowed-page-sizes="[10, 20, 25]"
        :show-info="true"
        :show-navigation-buttons="true"
        :show-page-size-selector="true"
        :visible="true"
      />
      <DxFilterRow :visible="false" />
      <DxColumnFixing :enabled="true" />
      <DxSearchPanel
        :visible="true"
        :width="140"
        :placeholder="$t('components.notificationsSettings.dataGrids.search')"
      />

      <!-- Modified IMEI Column with Registration Number -->
      <DxColumn
        data-field="imei"
        :caption="$t('components.notificationsSettings.dataGrids.imei')"
        :width="columnWidths.imei"
        header-cell-template="headerImeiTemplate"
        cell-template="imeiCellTemplate"
      />

      <!-- Other Columns -->
      <DxColumn
        data-field="notification_settings.send_by_sms"
        :caption="$t('components.notificationsSettings.dataGrids.sendBySms')"
        data-type="boolean"
        :width="columnWidths.sendBySms"
        alignment="center"
        header-cell-template="headerSmsTemplate"
      />
      <DxColumn
        data-field="notification_settings.send_by_email"
        :caption="$t('components.notificationsSettings.dataGrids.sendByEmail')"
        data-type="boolean"
        :width="columnWidths.sendByEmail"
        alignment="center"
        header-cell-template="headerEmailTemplate"
      />
      <DxColumn
        data-field="notification_settings.send_by_mercure"
        :caption="$t('components.notificationsSettings.dataGrids.sendByMercure')"
        data-type="boolean"
        :width="columnWidths.sendByMercure"
        alignment="center"
        header-cell-template="headerMercureTemplate"
      />

      <!-- Edit Column -->
      <DxColumn
        type="buttons"
        :width="columnWidths.buttons"
      >
        <DxButton
          :hint="$t('components.notificationsSettings.dataGrids.edit')"
          icon="edit"
          @click="openEditModal"
        />
      </DxColumn>

      <!-- Header Templates -->
      <template #headerImeiTemplate>
        <span><i class="fa fa-barcode" /> {{ $t('components.notificationsSettings.dataGrids.imeiOrRegistration') }}</span>
      </template>
      <template #headerSmsTemplate>
        <span><i class="fa fa-sms" /> {{ $t('components.notificationsSettings.dataGrids.sendBySms') }}</span>
      </template>
      <template #headerEmailTemplate>
        <span><i class="fa fa-envelope" /> {{ $t('components.notificationsSettings.dataGrids.sendByEmail') }}</span>
      </template>
      <template #headerMercureTemplate>
        <span><i class="fa fa-bolt" /> {{ $t('components.notificationsSettings.dataGrids.sendByMercure') }}</span>
      </template>

      <!-- Updated IMEI Cell Template -->
      <template #imeiCellTemplate="{ data }">
        <div>
          <!-- Registration Number or No Registration Message -->
          <div v-if="data.data.vehicles && data.data.vehicles.length > 0">
            <strong>
              {{ getRegistrationNumbers(data.data.vehicles) }}
            </strong>
          </div>
          <div v-else>
            <strong>{{ $t('components.notificationsSettings.dataGrids.noRegistration') }}</strong>
          </div>
          <!-- IMEI Number -->
          <div><small>{{ data.data.imei }}</small></div>
        </div>
      </template>
    </DxDataGrid>

    <!-- Modal component -->
    <div class="notifications-settings-custom-modal">
      <notifications-settings-modal
        ref="notificationsSettingsModalRef"
        @emittedRefreshGrid="refreshDataGrid"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxSelection,
  DxButton,
} from 'devextreme-vue/data-grid';
import { getNotificationsSettingsList } from '@/crud/device/getters';
import { checkIfAdminOrBoss } from '@/utils/tools';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import NotificationsSettingsModal from '@/components/content/_modals/NotificationsSettingsModal.vue';

export default defineComponent({
  name: 'NotificationsSettings',
  components: {
    DxDataGrid,
    DxHeaderFilter,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxColumnFixing,
    DxColumn,
    DxSearchPanel,
    DxSelection,
    DxButton,
    NotificationsSettingsModal,
  },
  data() {
    return {
      dataGridRef: 'notificationsSettingsGrid',
      selectedItems: [],
      showCheckBoxesMode: 'always',
      adminOrBoss: false,
      windowWidth: window.innerWidth,
      customDataSource: {
        load: (loadOptions: any) => {
          return getNotificationsSettingsList(loadOptions)
              .then((result) => {
                return {
                  data: result.data,
                  totalCount: result.totalCount,
                };
              })
              .catch(() => {
                this.$toast.error(
                    this.t('components.notificationsSettings.dataGrids.errorWhileLoading')
                );
                return {
                  data: [],
                  totalCount: 0,
                };
              });
        },
      },
    };
  },
  computed: {
    dataGrid() {
      const self: any = this;
      return self.$refs[self.dataGridRef].instance;
    },
    isMobile() {
      return this.windowWidth <= 768;
    },
    scrollingConfig() {
      if (this.isMobile) {
        return {
          mode: 'standard',
          useNativeScrolling: false,
          scrollByContent: false,
          scrollByThumb: false,
          showScrollbar: 'never',
          x: 'disabled',
          y: 'disabled'
        };
      } else {
        return {
          mode: 'standard',
          useNativeScrolling: true,
          scrollByContent: true,
          scrollByThumb: true,
          showScrollbar: 'onHover',
          x: 'auto',
          y: 'disabled'
        };
      }
    },
    columnWidths() {
      if (this.isMobile) {
        return {
          imei: 260, // increased by 10px
          sendBySms: 80,
          sendByEmail: 80,
          sendByMercure: 80,
          buttons: 60
        };
      } else {
        return {
          imei: 250,
          sendBySms: 160, // increased by 10px
          sendByEmail: 160, // increased by 10px
          sendByMercure: 210, // increased by 60px total
          buttons: 100
        };
      }
    },
  },
  setup() {
    const { t } = useI18n();
    return {
      quasar: useQuasar(),
      t,
    };
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    refreshDataGrid() {
      if (this.dataGrid) {
        this.dataGrid.refresh();
      }
    },
    onSelectionChanged(e) {
      this.selectedItems = e.component.getSelectedRowsData();
    },
    openEditModal(e) {
      const data = e.row.data;
      (this.$refs.notificationsSettingsModalRef as any).openEditModal(data);
    },
    getRegistrationNumbers(vehicles) {
      const registrations = vehicles.map((vehicle) => vehicle.registration).filter(Boolean);
      return registrations.join(', ');
    },
  },
  mounted() {
    this.adminOrBoss = checkIfAdminOrBoss();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
});
</script>
